import React, { ComponentProps } from "react";
import { useOutletContext } from "react-router-dom";

import AppContextValueProvider from "../../../App/Compositions/AppContextValueProvider";
import { IFlowStepProps } from "../../../Flows/types/IFlow";
import { Step } from "../../../types/Step";
import { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import QualifyStep, { IQualifyStepConfig } from "../Components/QualifyStep";
import QualifyStepForm from "../Components/QualifyStepForm";
import QualifyStepFormValidatorCache from "../Components/QualifyStepFormValidatorCache";
import QualifyStepWrapper, { IQualifyStepWrapperConfig } from "../Components/QualifyStepWrapper";
import useCreateNonCandidateContactResult from "../hooks/useCreateNonCandidateContactResult";
import useUpdatePatient from "../hooks/useUpdatePatient";

const CachedQualifyForm: IQualifyStepConfig["Form"] = props => (
  <QualifyStepFormValidatorCache {...props} Form={QualifyStepForm} />
);

const ComposedQualifyStep: IQualifyStepWrapperConfig["QualifyStep"] = props => (
  <QualifyStep {...props} Form={CachedQualifyForm} />
);

type IAppContextQualifyStepProps = Pick<
  ComponentProps<typeof QualifyStepWrapper>,
  | "patient"
  | "defaultCenter"
  | "bookingController"
  | "disqualifyingReasons"
  | "contactId"
  | "setPatient"
  | "setContactResult"
  | "onNonCandidateDisqualification"
  | "getNextStep"
>;

const ConfiguredAppContextQualifyStep: React.FC<IAppContextQualifyStepProps & IStepWrapperButtonProps> = props => {
  const flowStepProps = useOutletContext<IFlowStepProps>();
  return (
    <QualifyStepWrapper
      {...props}
      {...flowStepProps}
      QualifyStep={ComposedQualifyStep}
      UseCreateNonCandidateContactResult={useCreateNonCandidateContactResult}
      UseUpdatePatient={useUpdatePatient}
    />
  );
};

const AppContextQualifyStep: React.FC<IStepWrapperButtonProps> = AppContextValueProvider<
  IAppContextQualifyStepProps,
  ComponentProps<typeof ConfiguredAppContextQualifyStep>
>({
  Component: ConfiguredAppContextQualifyStep,
  map: appContextProps => {
    const {
      patient,
      defaultCenter,
      bookingController,
      disqualifyingReasonsCState,
      contactId,
      setPatient,
      setContactResult,
      triggerSavePatientNote,
      flowCState
    } = appContextProps;

    return {
      patient,
      defaultCenter,
      bookingController,
      disqualifyingReasons: disqualifyingReasonsCState || null,
      contactId,
      setPatient,
      setContactResult,
      onNonCandidateDisqualification: triggerSavePatientNote,
      getNextStep: () => flowCState.steps[Step.qualify]?.nextStep?.(appContextProps)
    };
  }
});

export default AppContextQualifyStep;
